import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/patterns";
import { TimeFormatChoice, datePreview } from "@/components/settings/TimeFormatChoice";

/** Settings → Display: each person can pick how times are written for them, or follow the company's choice */
export function MyDisplayPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user, refreshUser } = useAuth();
  const { success, error } = useToast();
  const [busy, setBusy] = useState(false);
  const company = user?.time_format_company === "24h" ? "24-hour" : "12-hour";
  const value = user?.time_format_user ?? "company";

  const change = async (v: string) => {
    if (busy || v === value) return;
    setBusy(true);
    try {
      await api.patch("/api/auth/me/preferences", { time_format: v === "company" ? null : v });
      await refreshUser();
      success("Saved", "Times now show the way you chose");
    } catch (err: any) {
      error("Could not save", err?.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const changeDate = async (v: string) => {
    if (busy || v === (user?.date_format_user ?? "company")) return;
    setBusy(true);
    try {
      await api.patch("/api/auth/me/preferences", { date_format: v === "company" ? null : v });
      await refreshUser();
      success("Saved", "Dates now show the way you chose");
    } catch (err: any) {
      error("Could not save", err?.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={embedded ? "space-y-4" : "mx-auto max-w-3xl space-y-4"}>
      {!embedded && <PageHeader title="Display" />}
      <section aria-label="Time format" className="space-y-3 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <div>
          <h2 className="text-sm font-semibold text-foreground">How times are written</h2>
          <p className="text-xs text-muted-foreground">Applies to you everywhere in the CRM. Your company's choice is {company}.</p>
        </div>
        <TimeFormatChoice
          name="my-time-format"
          value={value}
          onChange={change}
          options={[
            { value: "company", label: "Company default", format: null, note: `Follow the company (${company})` },
            { value: "12h", label: "12-hour", format: "12h" },
            { value: "24h", label: "24-hour", format: "24h" },
          ]}
        />
      </section>
      <section aria-label="Date order" className="space-y-3 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <div>
          <h2 className="text-sm font-semibold text-foreground">How dates are written</h2>
          <p className="text-xs text-muted-foreground">Applies to you everywhere in the CRM. Your company's choice is {user?.date_format_company === "mdy" ? "month first" : "day first"}.</p>
        </div>
        <TimeFormatChoice
          name="my-date-format"
          value={user?.date_format_user ?? "company"}
          onChange={changeDate}
          options={[
            { value: "company", label: "Company default", format: null, note: `Follow the company (${user?.date_format_company === "mdy" ? "month first" : "day first"})` },
            { value: "dmy", label: "Day first", format: null, preview: datePreview("dmy") },
            { value: "mdy", label: "Month first", format: null, preview: datePreview("mdy") },
          ]}
        />
      </section>

      <section aria-label="Time zone" className="space-y-1 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <h2 className="text-sm font-semibold text-foreground">Time zone</h2>
        <p className="text-xs text-muted-foreground">
          Taken from this device: <span className="font-mono text-foreground">{(user?.time_zone || "").replace(/_/g, " ") || "unknown"}</span>. Reminders, notices and emails written for you use it
          {user?.time_zone_company ? `; your company's is ${user.time_zone_company.replace(/_/g, " ")}` : ""}.
        </p>
      </section>
    </div>
  );
}
